import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading, StatCard } from "../../components/VB";
import RupiahInput from "../../components/RupiahInput";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Package, ShoppingCart, CreditCard, Search, X, Receipt, ScanLine, Eye, EyeOff, Printer, QrCode, Barcode, Settings as SettingsIcon, Bluetooth, Usb, Zap } from "lucide-react";
import * as Thermal from "../../lib/thermalPrinter";

const fmtIDR = (n) => "Rp " + (parseFloat(n || 0)).toLocaleString("id-ID", { maximumFractionDigits: 0 });
const UNIT_OPTIONS = ["Kg", "Gram", "Pcs", "Box", "Dus", "Slop", "Liter", "Ml", "Botol", "Pack", "Unit", "Porsi", "Sachet", "Lembar", "Meter", "Roll", "Kaleng", "Bungkus"];
const normalizeUnit = (value) => {
  const text = String(value || "").trim();
  return UNIT_OPTIONS.find((u) => u.toLowerCase() === text.toLowerCase()) || text || "Pcs";
};
const fmtDateTimeID = (value) => {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return value || "";
  return d.toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" });
};
const fmtMonthID = (value) => {
  if (!value) return "Semua Periode";
  const d = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
};
const fmtUnit = (p) => {
  const ukuran = String(p?.ukuran_satuan || "").trim();
  const satuan = String(p?.satuan || "").trim();
  if (ukuran && satuan) return `${ukuran} ${satuan}`;
  return ukuran || satuan || "-";
};
const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[c]));
const formatItemsForReport = (items) => (Array.isArray(items) ? items : []).map((i) => {
  const unit = fmtUnit(i);
  return `${i.nama || "Produk"}${unit !== "-" ? ` (${unit})` : ""} ×${i.qty || 0}`;
}).join(", ");
const printA4VendorSalesReport = (items, periode, vendorName = "") => {
  const visibleItems = Array.isArray(items) ? items : [];
  if (visibleItems.length === 0) return toast.error("Tidak ada data transaksi yang tampil untuk dicetak");
  const printWindow = window.open("", "_blank", "width=1024,height=768");
  if (!printWindow) return toast.error("Pop-up print diblokir browser. Izinkan pop-up lalu coba lagi.");

  const totalSales = visibleItems.reduce((a, t) => a + Number(t.total || 0), 0);
  const totalPaid = visibleItems.filter((t) => t.vendor_payment_status === "Lunas").reduce((a, t) => a + Number(t.total || 0), 0);
  const totalPending = visibleItems.filter((t) => t.vendor_payment_status !== "Lunas").reduce((a, t) => a + Number(t.total || 0), 0);
  const paidCount = visibleItems.filter((t) => t.vendor_payment_status === "Lunas").length;
  const pendingCount = visibleItems.length - paidCount;
  const rows = visibleItems.map((t, idx) => `
    <tr>
      <td class="center">${idx + 1}</td>
      <td>${escapeHtml(t.no_transaksi || "-")}</td>
      <td>${escapeHtml(t.tanggal || "-")}</td>
      <td>${escapeHtml(t.employee_nama || "-")}<br><span class="muted">${escapeHtml(t.no_karyawan || t.no_rekening_koperasi || "")}</span></td>
      <td>${escapeHtml(formatItemsForReport(t.items))}</td>
      <td class="right">${escapeHtml(fmtIDR(t.total || 0))}</td>
      <td class="center"><span class="badge ${t.vendor_payment_status === "Lunas" ? "paid" : "pending"}">${t.vendor_payment_status === "Lunas" ? "Sudah Dibayar" : "Menunggu Pembayaran"}</span></td>
    </tr>`).join("");

  printWindow.document.write(`<!doctype html>
<html>
<head>
  <title>Laporan Penjualan Vendor - ${escapeHtml(fmtMonthID(periode))}</title>
  <style>
    @page { size: A4 portrait; margin: 12mm; }
    * { box-sizing: border-box; }
    body { margin: 0; color: #000; background: #fff; font-family: Arial, Helvetica, sans-serif; font-size: 11px; line-height: 1.35; }
    .kop-yellow { background: #F2C744; height: 18px; margin-bottom: 8px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .kop { display: flex; align-items: center; gap: 14px; }
    .kop img { width: 74px; height: 74px; object-fit: contain; }
    .kop-text { flex: 1; font-family: "Times New Roman", Times, serif; }
    .company { font-size: 19px; font-weight: 700; letter-spacing: .3px; }
    .tagline { font-size: 12px; font-style: italic; margin-top: 2px; }
    .addr { font-size: 11px; margin-top: 4px; }
    .kop-line { border-bottom: 2.5px solid #000; margin-top: 8px; }
    h1 { text-align: center; font-size: 15px; margin: 14px 0 2px; text-transform: uppercase; }
    .subtitle { text-align: center; font-size: 12px; margin-bottom: 12px; }
    .meta { display: grid; grid-template-columns: 120px 10px 1fr 120px 10px 1fr; gap: 3px 4px; margin: 8px 0 12px; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 10px 0 12px; }
    .card { border: 1px solid #777; padding: 7px; min-height: 48px; }
    .card-label { font-size: 10px; color: #333; }
    .card-value { font-size: 14px; font-weight: 700; margin-top: 3px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #555; padding: 5px; vertical-align: top; }
    th { background: #e9eef7; text-align: center; font-weight: 700; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .center { text-align: center; }
    .right { text-align: right; }
    .muted { color: #555; font-size: 10px; }
    .badge { display: inline-block; padding: 2px 5px; border: 1px solid; font-weight: 700; font-size: 10px; white-space: nowrap; }
    .paid { color: #1f7a1f; background: #e5f7e5; border-color: #1f7a1f; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .pending { color: #9f6000; background: #fff3cd; border-color: #9f6000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .footer-note { margin-top: 12px; font-size: 10.5px; }
    .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 26px; text-align: center; }
    .sign-line { margin-top: 56px; border-top: 1px solid #000; padding-top: 4px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
  </style>
</head>
<body>
  <div class="kop-yellow"></div>
  <div class="kop">
    <img src="/logo-fivehoster.png" alt="PT. Fivehoster">
    <div class="kop-text">
      <div class="company">PT. FIVEHOSTER KARYA BANGSA</div>
      <div class="tagline">Jasa Penyedia Server, Pembuatan Software dan Website</div>
      <div class="addr">Head Office : Jl. Sawah Lunto RT.27 RW.07 Jeru Kec.Turen Kab.Malang</div>
      <div class="addr">Telp/Email : (031) 30004702 – support@fivehoster.co.id</div>
    </div>
  </div>
  <div class="kop-line"></div>

  <h1>Laporan Penjualan Vendor / Toko</h1>
  <div class="subtitle">Data penjualan dan status payment perusahaan ke vendor</div>

  <div class="meta">
    <div>Vendor / Toko</div><div>:</div><strong>${escapeHtml(vendorName || visibleItems[0]?.vendor_nama || "-")}</strong>
    <div>Periode</div><div>:</div><strong>${escapeHtml(fmtMonthID(periode))}</strong>
    <div>Jumlah Transaksi</div><div>:</div><strong>${visibleItems.length} transaksi</strong>
    <div>Tanggal Cetak</div><div>:</div><strong>${escapeHtml(fmtDateTimeID(new Date().toISOString()))}</strong>
  </div>

  <div class="summary">
    <div class="card"><div class="card-label">Total Penjualan</div><div class="card-value">${escapeHtml(fmtIDR(totalSales))}</div></div>
    <div class="card"><div class="card-label">Sudah Dibayar Perusahaan</div><div class="card-value">${escapeHtml(fmtIDR(totalPaid))}</div><div class="muted">${paidCount} transaksi</div></div>
    <div class="card"><div class="card-label">Menunggu Pembayaran</div><div class="card-value">${escapeHtml(fmtIDR(totalPending))}</div><div class="muted">${pendingCount} transaksi</div></div>
    <div class="card"><div class="card-label">Status Laporan</div><div class="card-value">${pendingCount > 0 ? "Ada Pending" : "Lunas Semua"}</div></div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width: 28px;">No</th>
        <th style="width: 92px;">No. Transaksi</th>
        <th style="width: 70px;">Tanggal</th>
        <th style="width: 120px;">Karyawan</th>
        <th>Item Penjualan</th>
        <th style="width: 88px;">Total</th>
        <th style="width: 105px;">Status Payment</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="footer-note">
    Laporan ini dicetak berdasarkan data transaksi yang sedang tampil pada halaman Riwayat Transaksi Saya sesuai filter periode yang dipilih.
  </div>

  <div class="signatures">
    <div>
      <div>Vendor / Toko</div>
      <div class="sign-line">${escapeHtml(vendorName || visibleItems[0]?.vendor_nama || "-")}</div>
    </div>
    <div>
      <div>Perusahaan</div>
      <div class="sign-line">PT. FIVEHOSTER KARYA BANGSA</div>
    </div>
  </div>
  <script>window.onload = function(){ window.focus(); window.print(); };</script>
</body>
</html>`);
  printWindow.document.close();
};
// Smart print: kalau printer thermal Bluetooth/USB sudah terhubung, kirim ESC/POS langsung
// (TANPA window.print()). Kalau belum terhubung / mode "system", fallback ke window print HTML.
const smartPrintReceipt = async (trx, vendor, printerCfg, fallbackVendorName = "") => {
  if (!trx) return toast.error("Belum ada transaksi untuk dicetak");
  const isBT = printerCfg?.connection_type === "bluetooth";
  const isUSB = printerCfg?.connection_type === "usb";
  const directReady = (isBT && Thermal.btIsConnected()) || (isUSB && Thermal.usbIsConnected());

  if (directReady) {
    try {
      await Thermal.printReceipt(trx, vendor || { nama: fallbackVendorName }, printerCfg);
      toast.success("Struk dikirim ke printer thermal");
      return;
    } catch (e) {
      toast.error("Direct print gagal: " + (e.message || e) + ". Beralih ke print dialog.");
      // fallback to window.print
    }
  } else if (isBT || isUSB) {
    toast.info("Printer belum terhubung. Buka tab Pengaturan Printer → klik Pasangkan Printer. Sementara, pakai print dialog.");
  }
  // Fallback to existing HTML print
  printThermalReceipt(trx, fallbackVendorName);
};

const printThermalReceipt = (trx, fallbackVendorName = "") => {
  if (!trx) return toast.error("Belum ada transaksi untuk dicetak");
  const printWindow = window.open("", "_blank", "width=360,height=640");
  if (!printWindow) return toast.error("Pop-up print diblokir browser. Izinkan pop-up lalu coba lagi.");  const items = Array.isArray(trx.items) ? trx.items : [];
  const rows = items.map((i) => {
    const unit = fmtUnit(i);
    const nama = `${i.nama || "Produk"}${unit !== "-" ? ` (${unit})` : ""}`;
    return `
      <tr>
        <td colspan="3" class="item-name">${escapeHtml(nama)}</td>
      </tr>
      <tr>
        <td>${escapeHtml(i.qty || 0)} x ${escapeHtml(fmtIDR(i.harga_satuan || 0))}</td>
        <td></td>
        <td class="right">${escapeHtml(fmtIDR(i.subtotal || 0))}</td>
      </tr>`;
  }).join("");
  const receiptCopy = (copyLabel) => `
  <section class="receipt-copy">
    <div class="center title">${escapeHtml(trx.vendor_nama || fallbackVendorName || "Vendor Koperasi")}</div>
    <div class="center bold">KOPERASI KARYAWAN</div>
    <div class="center bold">PT. FIVEHOSTER KARYA BANGSA</div>
    <div class="center copy-label">${escapeHtml(copyLabel)}</div>
    <div class="line"></div>
    <table>
      <tr><td>No</td><td>:</td><td>${escapeHtml(trx.no_transaksi || "-")}</td></tr>
      <tr><td>Tgl</td><td>:</td><td>${escapeHtml(fmtDateTimeID(trx.created_at || trx.tanggal))}</td></tr>
      <tr><td>Nama</td><td>:</td><td>${escapeHtml(trx.employee_nama || "-")}</td></tr>
      <tr><td>RFID</td><td>:</td><td>${escapeHtml(maskRfid(trx.rfid_card))}</td></tr>
    </table>
    <div class="line"></div>
    <table>${rows}</table>
    <div class="line"></div>
    <table>
      <tr class="total-row"><td>TOTAL</td><td></td><td class="right">${escapeHtml(fmtIDR(trx.total || 0))}</td></tr>
      <tr><td colspan="3">Struk Transaksi ini sebagai bukti sah pembayaran dengan kartu karyawan perusahaan</td></tr>
      <tr><td colspan="3">Sisa plafon: ${escapeHtml(fmtIDR(trx.plafon_sesudah_sisa || 0))}</td></tr>
    </table>
    <div class="line"></div>
    <div class="center muted">Terima kasih</div>
  </section>`;

  printWindow.document.write(`<!doctype html>
<html>
<head>
  <title>Struk ${escapeHtml(trx.no_transaksi || "POS")}</title>
  <style>
    @page { size: 58mm auto; margin: 0; }
    * { box-sizing: border-box; }
    body { width: 58mm; margin: 0; padding: 3mm; color: #000; background: #fff; font-family: "Consolas", "Courier New", monospace; font-size: 10px; line-height: 1.25; }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: 700; }
    .title { font-size: 13px; font-weight: 700; }
    .muted { font-size: 9px; }
    .line { border-top: 1px dashed #000; margin: 6px 0; }
    .copy-label { margin-top: 4px; border: 1px solid #000; padding: 2px 0; font-size: 10px; font-weight: 700; }
    .cut-space { min-height: 16mm; padding: 6mm 0 5mm; }
    .cut-line { border-top: 1px dashed #000; text-align: center; position: relative; }
    .cut-line span { background: #fff; padding: 0 4px; position: relative; top: -7px; font-size: 9px; font-weight: 700; }
    table { width: 100%; border-collapse: collapse; }
    td { vertical-align: top; padding: 1px 0; }
    .item-name { padding-top: 3px; word-break: break-word; }
    .total-row td { border-top: 1px dashed #000; padding-top: 5px; font-weight: 700; font-size: 12px; }
    @media print { body { width: 58mm; } .no-print { display: none; } }
  </style>
</head>
<body>
  ${receiptCopy("SALINAN KARYAWAN")}
  <div class="cut-space">
    <div class="cut-line"><span>✂ POTONG DI SINI ✂</span></div>
  </div>
  ${receiptCopy("SALINAN VENDOR / TOKO")}
  <script>window.onload = function(){ window.focus(); window.print(); };</script>
</body>
</html>`);
  printWindow.document.close();
};
const maskRfid = (value) => {
  const text = String(value || "");
  if (!text) return "-";
  if (text.length <= 4) return "•".repeat(text.length);
  return `${"•".repeat(text.length - 4)}${text.slice(-4)}`;
};

const SCAN_FORMATS = {
  rfid: ["qr_code"],
  product: ["qr_code", "ean_13", "ean_8", "code_128", "code_39", "code_93", "codabar", "upc_a", "upc_e", "itf", "data_matrix", "pdf417", "aztec"],
};

function CameraCodeScanner({ open, mode, onClose, onDetected, continuous = false, cartInfo = null }) {
  const videoRef = useRef(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [ready, setReady] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [lastDetected, setLastDetected] = useState(null); // {code, ts} for debounce

  useEffect(() => {
    if (!open) return undefined;
    let stream = null;
    let timer = null;
    let stopped = false;
    let lastCode = ""; let lastCodeTs = 0;

    const start = async () => {
      setError("");
      setInfo("");
      setReady(false);
      setManualCode("");
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Browser belum mendukung akses kamera. Gunakan Chrome/Edge terbaru atau input kode manual.");
        }
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setReady(true);

        const Detector = window.BarcodeDetector;
        if (!Detector) {
          setInfo("Preview kamera sudah aktif, tetapi browser ini belum mendukung auto scan QR/barcode. Ketik hasil scanner eksternal/manual di input bawah, atau gunakan Chrome/Edge terbaru.");
          return;
        }
        let formats = SCAN_FORMATS[mode] || SCAN_FORMATS.product;
        if (Detector.getSupportedFormats) {
          const supported = await Detector.getSupportedFormats();
          formats = formats.filter((f) => supported.includes(f));
        }
        if (formats.length === 0) {
          setInfo("Preview kamera aktif, tetapi format QR/barcode tidak didukung browser ini. Gunakan input manual atau browser lain.");
          return;
        }
        const detector = new Detector({ formats });
        timer = window.setInterval(async () => {
          if (stopped || !videoRef.current || videoRef.current.readyState < 2) return;
          try {
            const codes = await detector.detect(videoRef.current);
            const value = codes?.[0]?.rawValue?.trim();
            if (value) {
              const now = Date.now();
              // Debounce: skip if same code scanned within last 500ms (prevents a single
              // physical scan firing detector multiple times within ~700ms of holding code in view,
              // while still allowing user to consciously scan same product again to add qty).
              if (value === lastCode && now - lastCodeTs < 500) return;
              lastCode = value; lastCodeTs = now;
              setLastDetected({ code: value, ts: now });
              if (!continuous) stopped = true;
              onDetected(value);
            }
          } catch (_) {
            // Ignore transient frame decode errors.
          }
        }, 350);
      } catch (e) {
        setError(e.message || "Kamera tidak bisa dibuka.");
      }
    };

    start();
    return () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
      if (stream) stream.getTracks().forEach((track) => track.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, continuous]);

  const title = mode === "rfid" ? "Scan QR Code RFID Karyawan" : "Scan Barcode / QR Produk";
  const description = mode === "rfid"
    ? "Arahkan kamera ke QR Code yang berisi nomor RFID karyawan."
    : (continuous
      ? "Arahkan kamera ke barcode produk. Setiap produk yang terdeteksi langsung masuk ke keranjang. Scanner tetap aktif — scan beberapa barang berturut-turut tanpa menutup jendela ini."
      : "Arahkan kamera ke barcode/QR produk. Hasil scan akan masuk ke live search produk.");

  return (
    <Dialog open={open} onClose={onClose} title={title} width={460}
      footer={
        <>
          <button className="vb-btn" onClick={onClose}>{continuous ? "Selesai Scan" : "Tutup"}</button>
          {continuous && cartInfo?.onCheckout && (
            <button className="vb-btn-primary" onClick={cartInfo.onCheckout} disabled={!!cartInfo.checkoutDisabled} data-testid="scanner-checkout">
              Checkout ({cartInfo.formattedTotal || ""})
            </button>
          )}
          {(!continuous || !cartInfo?.onCheckout) && (
            <button className="vb-btn-primary" onClick={() => manualCode.trim() && onDetected(manualCode.trim())} disabled={!manualCode.trim()}>Gunakan Kode</button>
          )}
        </>
      }
    >
      <div style={{ fontSize: 12, marginBottom: 8, color: "#333" }}>{description}</div>
      {continuous && cartInfo && (
        <div style={{ marginBottom: 8, padding: 8, background: "#003399", color: "white", borderRadius: 4 }} data-testid="scanner-running-total">
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <span>Anggota: <strong>{cartInfo.anggotaNama || "-"}</strong></span>
            <span>Item: <strong>{cartInfo.itemCount || 0}</strong></span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginTop: 4 }}>
            <span>TOTAL:</span>
            <strong style={{ fontSize: 22 }}>{cartInfo.formattedTotal}</strong>
          </div>
          {cartInfo.formattedSisa !== undefined && (
            <div style={{ fontSize: 11, marginTop: 2, color: cartInfo.overPlafon ? "#ff8888" : "#a7f3a7" }}>
              Sisa plafon: {cartInfo.formattedSisa} {cartInfo.overPlafon && "⚠ MELEBIHI PLAFON"}
            </div>
          )}
        </div>
      )}
      <div className="vendor-scanner-box">
        <video ref={videoRef} className="vendor-scanner-video" muted playsInline />
        <div className="vendor-scanner-frame" />
        {!ready && !error && <div className="vendor-scanner-status">Membuka kamera...</div>}
      </div>
      {lastDetected && continuous && (
        <div style={{ marginTop: 8, padding: 8, background: "#e8f7e8", border: "1px solid #4f8a10", color: "#0a4d0a", fontSize: 12, fontFamily: "Consolas, monospace" }} data-testid="scanner-last-code">
          ✓ Terakhir terdeteksi: <strong>{lastDetected.code}</strong>
        </div>
      )}
      {info && <div style={{ marginTop: 8, padding: 8, background: "#fff8d7", border: "1px solid #d6b656", color: "#5f4300", fontSize: 12 }}>{info}</div>}
      {error && <div style={{ marginTop: 8, padding: 8, background: "#ffbaba", border: "1px solid #d8000c", color: "#d8000c", fontSize: 12 }}>{error}</div>}
      <div className="vb-form-row" style={{ marginTop: 10 }}>
        <label className="vb-label">Input manual / hasil scanner eksternal</label>
        <input className="vb-input" value={manualCode} onChange={(e) => setManualCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && manualCode.trim() && onDetected(manualCode.trim())} placeholder={mode === "rfid" ? "Nomor RFID dari QR" : "Kode barcode / SKU produk"} />
      </div>
    </Dialog>
  );
}

export default function VendorDashboard() {
  const [tab, setTab] = useState("pos");
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/koperasi/vendor/me").then(({ data }) => setMe(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  if (!me) return <Loading message="Memuat data vendor..." />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Window title={`Vendor Portal - ${me?.vendor?.nama || ""}`}>
        <div style={{ fontSize: 12, color: "#666" }}>
          {me?.vendor?.pemilik} · {me?.vendor?.kontak} · {me?.vendor?.email}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginTop: 10 }}>
          <StatCard label="Total Trx Lunas (dibayar Fivehoster)" value={fmtIDR(me?.stats?.total_paid)} testId="v-stat-paid" />
          <StatCard label="Pending Pembayaran" value={fmtIDR(me?.stats?.total_pending)} testId="v-stat-pending" />
          <StatCard label="Trx Pending" value={me?.stats?.count_pending} testId="v-stat-count-pending" />
        </div>
      </Window>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button className={`vb-btn ${tab === "pos" ? "vb-btn-primary" : ""}`} onClick={() => setTab("pos")} data-testid="v-tab-pos"><ScanLine className="w-3 h-3 inline mr-1" />Mesin POS</button>
        <button className={`vb-btn ${tab === "produk" ? "vb-btn-primary" : ""}`} onClick={() => setTab("produk")} data-testid="v-tab-produk"><Package className="w-3 h-3 inline mr-1" />Produk Saya</button>
        <button className={`vb-btn ${tab === "trx" ? "vb-btn-primary" : ""}`} onClick={() => setTab("trx")} data-testid="v-tab-trx"><Receipt className="w-3 h-3 inline mr-1" />Riwayat Transaksi</button>
        <button className={`vb-btn ${tab === "settings" ? "vb-btn-primary" : ""}`} onClick={() => setTab("settings")} data-testid="v-tab-settings"><SettingsIcon className="w-3 h-3 inline mr-1" />Pengaturan Printer</button>
      </div>

      {tab === "pos" && <PosTab vendorName={me?.vendor?.nama || ""} vendor={me?.vendor} />}
      {tab === "produk" && <ProdukTab />}
      {tab === "trx" && <RiwayatTab vendorName={me?.vendor?.nama || ""} vendor={me?.vendor} />}
      {tab === "settings" && <PrinterSettingsTab vendor={me?.vendor} />}
    </div>
  );
}

// ============ POS TAB ============
function PosTab({ vendorName = "", vendor = null }) {
  const [rfid, setRfid] = useState("");
  const [scanned, setScanned] = useState(null);
  const [produk, setProduk] = useState([]);
  const [cart, setCart] = useState([]); // {produk_id, qty, harga, nama}
  const [productSearch, setProductSearch] = useState("");
  const [showRfidInput, setShowRfidInput] = useState(false);
  const [showScannedRfid, setShowScannedRfid] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [posting, setPosting] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [scannerMode, setScannerMode] = useState(null); // rfid | product
  const [printerCfg, setPrinterCfg] = useState(null);

  useEffect(() => { api.get("/koperasi/produk").then(({ data }) => setProduk(data.filter((p) => p.status_aktif))); }, []);
  useEffect(() => { api.get("/koperasi/vendor-printer-config").then(({ data }) => setPrinterCfg(data)).catch(() => {}); }, []);

  const total = useMemo(() => cart.reduce((a, c) => a + c.harga * c.qty, 0), [cart]);
  const sisaSetelah = scanned ? scanned.sisa_plafon - total : 0;
  const isOverPlafon = !!scanned && cart.length > 0 && total > scanned.sisa_plafon;
  const checkoutDisabled = posting || !scanned || cart.length === 0 || isOverPlafon;
  const filteredProduk = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return produk;
    return produk.filter((p) => `${p.nama || ""} ${p.sku || ""} ${p.deskripsi || ""} ${p.satuan || ""} ${p.ukuran_satuan || ""}`.toLowerCase().includes(q));
  }, [produk, productSearch]);
  const displayedProduk = productSearch.trim() ? filteredProduk : filteredProduk.slice(0, 3);

  const clearProductSearch = () => setProductSearch("");
  const clearRfid = () => {
    setRfid("");
    setScanned(null);
    setShowScannedRfid(false);
    setCart([]);
  };

  const scanRfidCard = async (card) => {
    const value = String(card || "").trim();
    if (!value) return toast.error("Masukkan RFID");
    setScanning(true);
    try {
      const { data } = await api.get("/koperasi/scan-rfid", { params: { card: value } });
      setScanned(data); setShowScannedRfid(false); setCart([]); toast.success(`Terdeteksi: ${data.employee_nama}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setScanned(null); }
    setScanning(false);
  };
  const doScan = () => scanRfidCard(rfid);

  const handleCameraDetected = async (value) => {
    const code = String(value || "").trim();
    if (!code) return;
    const mode = scannerMode;
    if (mode === "rfid") {
      setScannerMode(null);  // close scanner once RFID found
      setRfid(code);
      await scanRfidCard(code);
      return;
    }
    // mode === "product": continuous scanning — auto-add to cart, KEEP scanner open
    if (!scanned) {
      toast.error("Scan kartu RFID anggota dulu sebelum scan produk");
      setScannerMode(null);
      return;
    }
    const exact = produk.find((p) => [p.sku, p.barcode, p.kode_barcode].some((v) => String(v || "").toLowerCase() === code.toLowerCase()));
    if (exact) {
      // Find current qty in cart (before adding) to show new qty in toast
      const existing = cart.find((c) => c.produk_id === exact.id);
      const newQty = (existing?.qty || 0) + 1;
      addItem(exact);
      toast.success(newQty > 1
        ? `+ ${exact.nama} → qty ${newQty} (subtotal ${fmtIDR(exact.harga * newQty)})`
        : `+ ${exact.nama} — ${fmtIDR(exact.harga)}`);
    } else {
      toast.error(`Barcode tidak ditemukan: ${code}`);
    }
    // Do NOT close scanner; user can scan multiple items
  };

  const addItem = (p) => {
    setCart((cur) => {
      const exist = cur.find((c) => c.produk_id === p.id);
      if (exist) return cur.map((c) => c.produk_id === p.id ? { ...c, qty: c.qty + 1 } : c);
      return [...cur, { produk_id: p.id, nama: p.nama, harga: p.harga, qty: 1, satuan: p.satuan, ukuran_satuan: p.ukuran_satuan }];
    });
  };
  const setQty = (id, qty) => {
    if (qty <= 0) setCart(cart.filter((c) => c.produk_id !== id));
    else setCart(cart.map((c) => c.produk_id === id ? { ...c, qty } : c));
  };

  const checkout = async () => {
    if (!scanned) return toast.error("Scan RFID dulu");
    if (cart.length === 0) return toast.error("Cart kosong");
    if (total > scanned.sisa_plafon) return toast.error(`Plafon tidak cukup! Total melebihi sisa plafon sebesar ${fmtIDR(total - scanned.sisa_plafon)}`);
    setPosting(true);
    try {
      const { data } = await api.post("/koperasi/transaksi", {
        rfid_card: scanned.rfid_card,
        items: cart.map((c) => ({ produk_id: c.produk_id, qty: c.qty })),
        catatan: "",
      });
      toast.success(`Transaksi ${data.no_transaksi} berhasil! Total ${fmtIDR(data.total)}`);
      setLastReceipt(data);
      setScanned(null); setCart([]); setRfid("");
      // Auto-print struk thermal jika ter-configured
      if (printerCfg && printerCfg.auto_print && printerCfg.connection_type !== "system") {
        try {
          await Thermal.printReceipt(data, vendor, printerCfg);
          toast.success("Struk dicetak ke printer thermal");
        } catch (printErr) {
          toast.error("Auto-print gagal: " + (printErr.message || printErr) + ". Cetak manual via tombol di bawah.");
        }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setPosting(false);
  };

  return (
    <Window title="Mesin POS — Scan RFID Anggota Koperasi">
      <div className="vendor-pos-grid">
        {/* Left: scan + cart */}
        <div>
          <div style={{ padding: 10, background: "#003399", color: "white", borderRadius: 4, marginBottom: 10 }}>
            <label className="vb-label" style={{ color: "white" }}>SCAN / KETIK NOMOR RFID:</label>
            <div className="vendor-rfid-row">
              <input type={showRfidInput ? "text" : "password"} className="vb-input vendor-rfid-input" style={{ flex: 1, fontFamily: "Consolas, monospace", fontSize: 18, color: "#000", background: "#fff" }} value={rfid} onChange={(e) => setRfid(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()} placeholder="Tap kartu RFID atau ketik" autoFocus data-testid="pos-rfid" />
              {rfid && <button type="button" className="vb-btn-danger" onClick={clearRfid} title="Hapus nomor RFID" data-testid="pos-rfid-clear"><X className="w-3 h-3" /></button>}
              <button type="button" className="vb-btn" onClick={() => setShowRfidInput(!showRfidInput)} title={showRfidInput ? "Sembunyikan RFID" : "Lihat RFID"} data-testid="pos-rfid-toggle">
                {showRfidInput ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
              <button type="button" className="vb-btn" onClick={() => setScannerMode("rfid")} title="Scan QR Code RFID dengan kamera" data-testid="pos-rfid-qr-scan">
                <QrCode className="w-3 h-3 inline mr-1" />QR
              </button>
              <button className="vb-btn-primary" onClick={doScan} disabled={scanning} data-testid="pos-scan">
                <Search className="w-3 h-3 inline mr-1" />Scan
              </button>
            </div>
          </div>

          {scanned && (
            <div style={{ padding: 10, background: "#e8f7e8", border: "1px solid #4f8a10", marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#003399" }}>{scanned.employee_nama}</div>
              <div style={{ fontSize: 11 }}>No. Karyawan: {scanned.no_karyawan} · Rek Koperasi: {scanned.no_rekening_koperasi}</div>
              <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11 }}>
                <span>RFID: <strong style={{ fontFamily: "Consolas, monospace" }}>{showScannedRfid ? scanned.rfid_card : maskRfid(scanned.rfid_card)}</strong></span>
                <button type="button" className="vb-btn" style={{ padding: "1px 6px" }} onClick={() => setShowScannedRfid(!showScannedRfid)} title={showScannedRfid ? "Sembunyikan RFID" : "Lihat RFID"} data-testid="pos-scanned-rfid-toggle">
                  {showScannedRfid ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
              </div>
              <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, fontSize: 12 }}>
                <div>Plafon:<br /><strong>{fmtIDR(scanned.plafon)}</strong></div>
                <div>Terhutang:<br /><strong style={{ color: "#d8000c" }}>{fmtIDR(scanned.terhutang)}</strong></div>
                <div>Sisa:<br /><strong style={{ color: "#4f8a10" }}>{fmtIDR(scanned.sisa_plafon)}</strong></div>
              </div>
            </div>
          )}

          {lastReceipt && (
            <div style={{ padding: 10, background: "#fff8d7", border: "1px solid #d6b656", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12 }}>
                Transaksi terakhir: <strong style={{ color: "#003399" }}>{lastReceipt.no_transaksi}</strong> · {fmtIDR(lastReceipt.total)}
              </div>
              <button type="button" className="vb-btn-primary" onClick={() => smartPrintReceipt(lastReceipt, vendor, printerCfg, vendorName)} data-testid="pos-print-last-receipt">
                <Printer className="w-3 h-3 inline mr-1" />Cetak Struk {printerCfg && (printerCfg.connection_type === "bluetooth" || printerCfg.connection_type === "usb") ? "(Direct)" : "58mm"}
              </button>
            </div>
          )}

          <h3 className="vb-h1" style={{ marginBottom: 6 }}><ShoppingCart className="w-4 h-4 inline mr-1" />Keranjang</h3>
          {cart.length === 0 ? <EmptyState message="Keranjang kosong. Pilih produk →" /> : (
            <div className="vendor-cart-table-wrap">
              <table className="vb-table">
                <thead><tr><th>Produk</th><th style={{ textAlign: "right" }}>Harga</th><th style={{ textAlign: "center" }}>Qty</th><th style={{ textAlign: "right" }}>Subtotal</th><th></th></tr></thead>
                <tbody>
                  {cart.map((c) => (
                    <tr key={c.produk_id}>
                      <td><strong>{c.nama}</strong><div style={{ fontSize: 10, color: "#666" }}>{fmtUnit(c)}</div></td>
                      <td style={{ textAlign: "right" }}>{fmtIDR(c.harga)}</td>
                      <td style={{ textAlign: "center" }}>
                        <button className="vb-btn" style={{ padding: "1px 6px" }} onClick={() => setQty(c.produk_id, c.qty - 1)}>−</button>
                        <span style={{ margin: "0 8px", fontWeight: 700 }}>{c.qty}</span>
                        <button className="vb-btn" style={{ padding: "1px 6px" }} onClick={() => setQty(c.produk_id, c.qty + 1)}>+</button>
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtIDR(c.harga * c.qty)}</td>
                      <td><button className="vb-btn-danger" style={{ padding: "1px 5px" }} onClick={() => setQty(c.produk_id, 0)}><X className="w-3 h-3" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {cart.length > 0 && (
            <div style={{ marginTop: 10, padding: 10, background: "#003399", color: "white", borderRadius: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span>TOTAL:</span><strong style={{ fontSize: 22 }}>{fmtIDR(total)}</strong>
              </div>
              {scanned && (
                <div style={{ fontSize: 11, marginTop: 4, color: sisaSetelah < 0 ? "#ff8888" : "#a7f3a7" }}>
                  Sisa plafon setelah transaksi: {fmtIDR(sisaSetelah)}
                </div>
              )}
              {isOverPlafon && (
                <div style={{ marginTop: 8, padding: 8, background: "#ffbaba", color: "#d8000c", border: "1px solid #d8000c", fontSize: 12, fontWeight: 700 }} data-testid="pos-plafon-warning">
                  WARNING: Total belanja melebihi sisa plafon sebesar {fmtIDR(total - scanned.sisa_plafon)}. Checkout tidak dapat dilakukan.
                </div>
              )}
              <button className="vb-btn-primary" style={{ width: "100%", marginTop: 10, padding: "8px", fontSize: 14 }} onClick={checkout} disabled={checkoutDisabled} data-testid="pos-checkout">
                <CreditCard className="w-4 h-4 inline mr-2" />{posting ? "Memproses..." : isOverPlafon ? "CHECKOUT DITOLAK — PLAFON KURANG" : "CHECKOUT (Terhutang → Potong Gaji)"}
              </button>
            </div>
          )}
        </div>

        {/* Right: produk list */}
        <div>
          <h3 className="vb-h1" style={{ marginBottom: 6 }}><Package className="w-4 h-4 inline mr-1" />Produk Tersedia</h3>
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <input className="vb-input" style={{ flex: 1 }} value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Live search nama, SKU, atau satuan" data-testid="pos-product-search" />
            <button type="button" className="vb-btn" onClick={() => setScannerMode("product")} title="Scan barcode produk dengan kamera" data-testid="pos-product-barcode-scan"><Barcode className="w-3 h-3" /></button>
            {productSearch && <button type="button" className="vb-btn" onClick={clearProductSearch} data-testid="pos-product-search-clear"><X className="w-3 h-3" /></button>}
          </div>
          {productSearch ? <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>{filteredProduk.length} produk ditemukan untuk “{productSearch}”. Scroll daftar bila hasilnya banyak.</div> : produk.length > 3 && <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>Menampilkan 3 produk awal. Gunakan live search untuk melihat produk lainnya.</div>}
          {produk.length === 0 ? <EmptyState message="Belum ada produk. Upload di tab Produk Saya." /> : (
            displayedProduk.length === 0 ? <EmptyState message="Produk tidak ditemukan. Ubah kata kunci pencarian." /> : <div className={`vendor-products-list ${productSearch ? "vendor-products-list-search" : ""}`}>
              {displayedProduk.map((p) => (
                <button key={p.id} onClick={() => addItem(p)} disabled={!scanned} style={{ padding: 8, textAlign: "left", border: "1px solid #828790", background: scanned ? "white" : "#f5f5f5", cursor: scanned ? "pointer" : "not-allowed" }} data-testid={`pos-add-${p.id}`}>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{p.nama}</div>
                  <div style={{ fontSize: 10, color: "#666" }}>{p.sku || "Tanpa SKU"} · {fmtUnit(p)}</div>
                  <div style={{ fontSize: 13, color: "#003399", fontWeight: 700, marginTop: 2 }}>{fmtIDR(p.harga)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <CameraCodeScanner
        open={!!scannerMode}
        mode={scannerMode}
        continuous={scannerMode === "product"}
        onClose={() => setScannerMode(null)}
        onDetected={handleCameraDetected}
        cartInfo={scannerMode === "product" && scanned ? {
          anggotaNama: scanned.employee_nama,
          itemCount: cart.reduce((a, c) => a + c.qty, 0),
          formattedTotal: fmtIDR(total),
          formattedSisa: fmtIDR(sisaSetelah),
          overPlafon: isOverPlafon,
          onCheckout: async () => { setScannerMode(null); await checkout(); },
          checkoutDisabled: checkoutDisabled,
        } : null}
      />
    </Window>
  );
}

// ============ PRODUK TAB ============
function ProdukTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama: "", sku: "", harga: 0, ukuran_satuan: "1", satuan: "Pcs", deskripsi: "", status_aktif: true };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);
  const [search, setSearch] = useState("");
  const [skuScanner, setSkuScanner] = useState(false);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => `${p.nama || ""} ${p.sku || ""} ${p.deskripsi || ""} ${p.satuan || ""} ${p.ukuran_satuan || ""}`.toLowerCase().includes(q));
  }, [items, search]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/koperasi/produk"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (!String(form.nama || "").trim()) return toast.error("Nama Produk wajib diisi");
      if (!String(form.satuan || "").trim()) return toast.error("Jenis satuan wajib diisi");
      const payload = {
        nama: String(form.nama || "").trim(),
        sku: String(form.sku || "").trim(),
        harga: parseFloat(form.harga || 0),
        ukuran_satuan: String(form.ukuran_satuan || "").trim(),
        satuan: normalizeUnit(form.satuan),
        deskripsi: String(form.deskripsi || ""),
        status_aktif: !!form.status_aktif,
      };
      if (editId) await api.put(`/koperasi/produk/${editId}`, payload);
      else await api.post("/koperasi/produk", payload);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/koperasi/produk/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Window title="Produk Saya">
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-produk"><Plus className="w-3 h-3 inline mr-1" />Tambah Produk</button>
        <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 240 }}>
          <input className="vb-input" style={{ flex: 1 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk, SKU, atau satuan" data-testid="produk-search" />
          {search && <button type="button" className="vb-btn" onClick={() => setSearch("")} data-testid="produk-search-clear"><X className="w-3 h-3" /></button>}
        </div>
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada produk. Upload sekarang." /> : filteredItems.length === 0 ? <EmptyState message="Produk tidak ditemukan. Ubah kata kunci pencarian." /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table className="vb-table">
          <thead><tr><th>Nama</th><th>SKU</th><th style={{ textAlign: "right" }}>Harga</th><th style={{ textAlign: "center" }}>Satuan</th><th>Status</th><th>Aksi</th></tr></thead>
          <tbody>
            {filteredItems.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.nama}</strong><div style={{ fontSize: 10, color: "#666" }}>{p.deskripsi}</div></td>
                <td style={{ fontFamily: "Consolas, monospace" }}>{p.sku}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtIDR(p.harga)}</td>
                <td style={{ textAlign: "center" }}>{fmtUnit(p)}</td>
                <td>{p.status_aktif ? <span className="vb-badge vb-badge-hadir">Aktif</span> : <span className="vb-badge vb-badge-tidakhadir">Nonaktif</span>}</td>
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm({ ...empty, ...p, ukuran_satuan: p.ukuran_satuan || "1", satuan: normalizeUnit(p.satuan) }); setEditId(p.id); setOpen(true); }}><Pencil className="w-3 h-3" /></button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(p)}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Produk" : "Tambah Produk"} width={500}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={save} data-testid="produk-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Nama Produk*</label>
          <input className="vb-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required /></div>
        <div className="vb-form-row"><label className="vb-label">SKU / Kode / Barcode</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input className="vb-input" style={{ flex: 1 }} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Ketik manual atau scan barcode" data-testid="produk-sku" />
            <button type="button" className="vb-btn" onClick={() => setSkuScanner(true)} title="Scan barcode produk" data-testid="produk-sku-scan"><Barcode className="w-3 h-3 inline mr-1" />Scan</button>
          </div>
          <span style={{ fontSize: 11, color: "#666", marginTop: 2 }}>💡 Gunakan tombol Scan untuk memindai barcode produk via kamera (kasir akan match produk dengan SKU ini saat scan POS).</span>
        </div>
        <div className="vb-form-row"><label className="vb-label">Harga (IDR)*</label>
          <RupiahInput value={form.harga} onChange={(v) => setForm({ ...form, harga: v })} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div className="vb-form-row"><label className="vb-label">Ukuran / Isi</label>
            <input className="vb-input" value={form.ukuran_satuan} onChange={(e) => setForm({ ...form, ukuran_satuan: e.target.value })} placeholder="Contoh: 1, 250, 500" /></div>
          <div className="vb-form-row"><label className="vb-label">Jenis Satuan*</label>
            <select className="vb-select" style={{ color: "#000", backgroundColor: "#fff" }} value={normalizeUnit(form.satuan)} onChange={(e) => setForm({ ...form, satuan: e.target.value })}>
              {UNIT_OPTIONS.map((u) => <option key={u} value={u} style={{ color: "#000", backgroundColor: "#fff" }}>{u}</option>)}
              {form.satuan && !UNIT_OPTIONS.some((u) => u.toLowerCase() === String(form.satuan).toLowerCase()) && <option value={form.satuan} style={{ color: "#000", backgroundColor: "#fff" }}>{form.satuan}</option>}
            </select>
          </div>
        </div>
        <div className="vb-form-row"><label className="vb-label">Deskripsi</label>
          <textarea rows={2} className="vb-textarea" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} /></div>
        <div className="vb-form-row">
          <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!form.status_aktif} onChange={(e) => setForm({ ...form, status_aktif: e.target.checked })} /> Aktif (tampil di POS)</label>
        </div>
      </Dialog>
      <CameraCodeScanner
        open={skuScanner}
        mode="product"
        continuous={false}
        onClose={() => setSkuScanner(false)}
        onDetected={(code) => { setForm((f) => ({ ...f, sku: code })); setSkuScanner(false); toast.success(`Barcode dipasang: ${code}`); }}
      />
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus produk "${del?.nama}"?`} />
    </Window>
  );
}

// ============ RIWAYAT TAB ============
function RiwayatTab({ vendorName = "", vendor = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulan, setBulan] = useState(new Date().toISOString().slice(0, 7));
  const [printerCfg, setPrinterCfg] = useState(null);

  useEffect(() => { api.get("/koperasi/vendor-printer-config").then(({ data }) => setPrinterCfg(data)).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/koperasi/transaksi", { params: { bulan } }); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bulan]);

  const total = items.reduce((a, i) => a + i.total, 0);
  return (
    <Window title="Riwayat Transaksi Saya">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input type="month" className="vb-input" style={{ width: 160 }} value={bulan} onChange={(e) => setBulan(e.target.value)} />
        <span style={{ fontSize: 12 }}>Total: <strong style={{ color: "#003399" }}>{fmtIDR(total)}</strong> ({items.length} trx)</span>
        <button type="button" className="vb-btn-primary" onClick={() => printA4VendorSalesReport(items, bulan, vendorName)} disabled={loading || items.length === 0} data-testid="vendor-print-sales-report">
          <Printer className="w-3 h-3 inline mr-1" />Print Laporan A4
        </button>
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada transaksi periode ini" /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table className="vb-table">
          <thead><tr><th>No. Trx</th><th>Tgl</th><th>Anggota</th><th>Items</th><th style={{ textAlign: "right" }}>Total</th><th>Status Bayar Fivehoster</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id}>
                <td style={{ fontFamily: "Consolas, monospace", color: "#003399" }}>{t.no_transaksi}</td>
                <td style={{ fontSize: 11 }}>{t.tanggal}</td>
                <td>{t.employee_nama}</td>
                <td style={{ fontSize: 11 }}>{t.items.map((i) => `${i.nama}${fmtUnit(i) !== "-" ? ` (${fmtUnit(i)})` : ""} ×${i.qty}`).join(", ")}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmtIDR(t.total)}</td>
                <td>{t.vendor_payment_status === "Lunas" ? <span className="vb-badge vb-badge-approved">Sudah Dibayar</span> : <span className="vb-badge vb-badge-pending">Menunggu Pembayaran</span>}</td>
                <td><button type="button" className="vb-btn" style={{ padding: "2px 6px" }} onClick={() => smartPrintReceipt(t, vendor, printerCfg, vendorName)}><Printer className="w-3 h-3 inline mr-1" />Cetak</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Window>
  );
}

// ============ PRINTER SETTINGS TAB ============
function PrinterSettingsTab({ vendor }) {
  const empty = { paper_width: "58mm", connection_type: "bluetooth", device_name: "", auto_print: true, header_lines: [], footer_lines: ["Terima kasih!", "Simpan struk ini sebagai bukti."], cut_paper: true, open_drawer: false };
  const [cfg, setCfg] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pairing, setPairing] = useState(false);
  const [connected, setConnected] = useState(false);
  const [headerText, setHeaderText] = useState("");
  const [footerText, setFooterText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/koperasi/vendor-printer-config");
      setCfg(data);
      setHeaderText((data.header_lines || []).join("\n"));
      setFooterText((data.footer_lines || []).join("\n"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Periodic check of connection state
  useEffect(() => {
    const id = setInterval(() => {
      setConnected(cfg.connection_type === "bluetooth" ? Thermal.btIsConnected() : cfg.connection_type === "usb" ? Thermal.usbIsConnected() : false);
    }, 1000);
    return () => clearInterval(id);
  }, [cfg.connection_type]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...cfg,
        header_lines: headerText.split("\n").map((s) => s.trim()).filter(Boolean),
        footer_lines: footerText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/koperasi/vendor-printer-config", payload);
      setCfg(data);
      toast.success("Konfigurasi printer disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const pair = async () => {
    setPairing(true);
    try {
      let info;
      if (cfg.connection_type === "bluetooth") info = await Thermal.btConnect();
      else if (cfg.connection_type === "usb") info = await Thermal.usbConnect();
      else { toast.info("Mode 'system' menggunakan dialog browser; tidak perlu pairing."); setPairing(false); return; }
      setCfg((c) => ({ ...c, device_name: info.name || "" }));
      setConnected(true);
      toast.success(`Printer "${info.name || "tanpa nama"}" tersambung. Klik Simpan untuk menyimpan konfigurasi.`);
    } catch (e) { toast.error(e.message || String(e)); }
    setPairing(false);
  };

  const disconnect = async () => {
    try { await Thermal.btDisconnect(); } catch (_) { /* noop */ }
    setConnected(false);
    toast.info("Printer di-disconnect");
  };

  const testPrint = async () => {
    if (!connected && cfg.connection_type !== "system") {
      return toast.error("Pasangkan printer dulu (klik tombol Pasangkan Printer)");
    }
    const dummy = {
      no_transaksi: "TEST-001",
      tanggal: new Date().toISOString().slice(0, 10),
      created_at: new Date().toISOString(),
      vendor_nama: vendor?.nama || "VENDOR",
      employee_nama: "TEST KARYAWAN",
      no_karyawan: "EMP-000",
      no_rekening_koperasi: "KOP-000",
      items: [
        { nama: "Indomie Goreng", qty: 2, harga_satuan: 3500, subtotal: 7000 },
        { nama: "Kopi Sachet", qty: 1, harga_satuan: 2500, subtotal: 2500 },
        { nama: "Air Mineral 600ml", qty: 1, harga_satuan: 4000, subtotal: 4000 },
      ],
      total: 13500,
      periode_potong: new Date().toISOString().slice(0, 7),
      catatan: "Test print struk thermal",
    };
    try {
      const previewCfg = { ...cfg, header_lines: headerText.split("\n").map((s) => s.trim()).filter(Boolean), footer_lines: footerText.split("\n").map((s) => s.trim()).filter(Boolean) };
      await Thermal.printReceipt(dummy, vendor, previewCfg);
      toast.success("Test print terkirim ke printer");
    } catch (e) { toast.error(e.message || "Gagal print"); }
  };

  if (loading) return <Loading />;
  return (
    <Window title="Pengaturan Printer Thermal">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <h3 className="vb-h1" style={{ marginBottom: 8 }}>Koneksi Printer</h3>
          <div style={{ padding: 10, background: connected ? "#e8f7e8" : "#fffbe6", border: "1px solid " + (connected ? "#4f8a10" : "#d4a017"), marginBottom: 10 }}>
            <div style={{ fontSize: 12 }}>Status: <strong>{connected ? "TERHUBUNG" : "BELUM TERHUBUNG"}</strong></div>
            {cfg.device_name && <div style={{ fontSize: 11, color: "#666" }}>Device terakhir: {cfg.device_name}</div>}
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              <button className="vb-btn-primary" onClick={pair} disabled={pairing || cfg.connection_type === "system"} data-testid="printer-pair">
                {cfg.connection_type === "bluetooth" ? <Bluetooth className="w-3 h-3 inline mr-1" /> : <Usb className="w-3 h-3 inline mr-1" />}
                {pairing ? "Mencari..." : "Pasangkan Printer"}
              </button>
              {connected && cfg.connection_type === "bluetooth" && (
                <button className="vb-btn" onClick={disconnect} data-testid="printer-disconnect">Disconnect</button>
              )}
              <button className="vb-btn" onClick={testPrint} disabled={!connected && cfg.connection_type !== "system"} data-testid="printer-test">
                <Zap className="w-3 h-3 inline mr-1" />Test Print Struk
              </button>
            </div>
          </div>

          <div className="vb-form-row">
            <label className="vb-label">Tipe Koneksi*</label>
            <select className="vb-select" value={cfg.connection_type} onChange={(e) => { setCfg({ ...cfg, connection_type: e.target.value, device_name: "" }); setConnected(false); }} data-testid="printer-conntype">
              <option value="bluetooth">Bluetooth (BLE) — printer 58mm/80mm wireless</option>
              <option value="usb">USB — printer USB ESC/POS langsung ke laptop</option>
              <option value="system">System Print Dialog (Cetak HTML, ada preview)</option>
            </select>
            <span style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
              Bluetooth &amp; USB butuh Chrome/Edge desktop atau Chrome Android. Bluetooth pairing dilakukan oleh OS.
            </span>
          </div>

          <div className="vb-form-row">
            <label className="vb-label">Lebar Kertas*</label>
            <select className="vb-select" value={cfg.paper_width} onChange={(e) => setCfg({ ...cfg, paper_width: e.target.value })} data-testid="printer-width">
              <option value="58mm">58mm (32 kolom karakter)</option>
              <option value="80mm">80mm (48 kolom karakter)</option>
            </select>
          </div>

          <div className="vb-form-row">
            <label style={{ fontSize: 13, fontWeight: 600 }}>
              <input type="checkbox" checked={!!cfg.auto_print} onChange={(e) => setCfg({ ...cfg, auto_print: e.target.checked })} data-testid="printer-autoprint" />
              {" "}Auto-print struk setelah checkout (tanpa dialog browser)
            </label>
            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
              Saat aktif, setiap checkout POS langsung kirim ke printer Bluetooth/USB tanpa membuka window preview.
            </div>
          </div>

          <div className="vb-form-row">
            <label style={{ fontSize: 12 }}>
              <input type="checkbox" checked={!!cfg.cut_paper} onChange={(e) => setCfg({ ...cfg, cut_paper: e.target.checked })} />
              {" "}Kirim perintah <strong>cut paper</strong> di akhir struk (printer dengan auto-cutter)
            </label>
          </div>
          <div className="vb-form-row">
            <label style={{ fontSize: 12 }}>
              <input type="checkbox" checked={!!cfg.open_drawer} onChange={(e) => setCfg({ ...cfg, open_drawer: e.target.checked })} />
              {" "}Buka cash drawer setelah print (jika printer connect ke drawer)
            </label>
          </div>
        </div>

        <div>
          <h3 className="vb-h1" style={{ marginBottom: 8 }}>Custom Header / Footer Struk</h3>
          <div className="vb-form-row">
            <label className="vb-label">Header Lines (1 baris per line, kosongkan jika tidak perlu)</label>
            <textarea rows={4} className="vb-textarea" value={headerText} onChange={(e) => setHeaderText(e.target.value)} placeholder="Mis:&#10;Jl. Sawah Lunto No. 1&#10;Telp 0341-12345" data-testid="printer-header" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Footer Lines</label>
            <textarea rows={4} className="vb-textarea" value={footerText} onChange={(e) => setFooterText(e.target.value)} placeholder="Mis:&#10;Terima kasih!&#10;Simpan struk ini" data-testid="printer-footer" />
          </div>

          <div style={{ padding: 10, background: "#f0f0f0", border: "1px solid #828790", fontSize: 11, fontFamily: "Consolas, monospace", whiteSpace: "pre", marginTop: 8 }}>
{`================================
${(vendor?.nama || "VENDOR").toUpperCase().padStart((32 + (vendor?.nama || "").length) / 2)}
${headerText.split("\n").map((l) => l.trim()).filter(Boolean).join("\n")}
KOPERASI PT FIVEHOSTER
--------------------------------
No   : TEST-001
Tgl  : ${new Date().toLocaleString("id-ID")}
--------------------------------
Indomie Goreng
  2x Rp3,500         Rp7,000
Kopi Sachet
  1x Rp2,500         Rp2,500
--------------------------------
TOTAL          Rp9,500
--------------------------------
${footerText.split("\n").map((l) => l.trim()).filter(Boolean).join("\n")}
================================`}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 6 }}>
        <button className="vb-btn-primary" onClick={save} disabled={saving} data-testid="printer-save">
          {saving ? "Menyimpan..." : "Simpan Konfigurasi"}
        </button>
      </div>
    </Window>
  );
}

